import React, { useRef, useState } from "react";
import moment from "moment";
import "./css/customCalendar.css";
import userStore from "../../zustand/userStore";
import timetableData from "./TimeTable";

const TeachersPlanner = () => {
  const [currentDate, setCurrentDate] = useState(moment());
  const [view, setView] = useState("week");
  const dateInputRef = useRef(null);
  const userData = userStore((state) => state.user);

  const teacherTimetable = timetableData;

  const getTimetableForDate = (date) => {
    const dayName = date.format("dddd");
    return teacherTimetable.filter((item) => item.day === dayName);
  };

  const handlePrev = () => {
    setCurrentDate((prev) =>
      view === "month"
        ? prev.clone().subtract(1, "month")
        : view === "week"
        ? prev.clone().subtract(1, "week")
        : prev.clone().subtract(1, "day")
    );
  };

  const handleNext = () => {
    setCurrentDate((prev) =>
      view === "month"
        ? prev.clone().add(1, "month")
        : view === "week"
        ? prev.clone().add(1, "week")
        : prev.clone().add(1, "day")
    );
  };

  const RenderMonthView = () => {
    const firstDayOfMonth = currentDate.clone().startOf("month");
    const daysInMonth = currentDate.daysInMonth();
    const startDay = firstDayOfMonth.day();

    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
      <div className="table-responsive">
        <div className="calendar-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}

          {Array.from({ length: startDay }).map((_, index) => (
            <div key={`empty-${index}`} className="calendar-day empty"></div>
          ))}

          {days.map((day) => {
            const date = currentDate.clone().date(day);
            const dayTimetable = getTimetableForDate(date);

            return (
              <div key={day} className="calendar-day">
                <div className="day-number">{day}</div>
                {dayTimetable.length > 0 && (
                  <div className="event">
                    {dayTimetable.length} class
                    {dayTimetable.length !== 1 ? "es" : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = currentDate.clone().startOf("week");
    const weekDays = Array.from({ length: 7 }).map((_, index) =>
      startOfWeek.clone().add(index, "days")
    );

    const renderTimeBlocks = (dayTimetable) => {
      const sortedClasses = [...dayTimetable].sort((a, b) =>
        moment(a.start_time, "HH:mm:ss").diff(moment(b.start_time, "HH:mm:ss"))
      );

      const blocks = [];
      let currentTime = moment("09:00", "HH:mm");
      const endTime = moment("16:30", "HH:mm");

      for (let classItem of sortedClasses) {
        const classStart = moment(classItem.start_time, "HH:mm:ss");
        const classEnd = moment(classItem.end_time, "HH:mm:ss");

        // Free time before class
        if (classStart.isAfter(currentTime)) {
          blocks.push({
            type: "free",
            start: currentTime.clone(),
            end: classStart.clone(),
          });
        }

        // Occupied time (class)
        blocks.push({
          type: "occupied",
          start: classStart.clone(),
          end: classEnd.clone(),
          classItem,
        });

        currentTime = classEnd.clone();
      }

      // Free time after last class
      if (currentTime.isBefore(endTime)) {
        blocks.push({
          type: "free",
          start: currentTime,
          end: endTime,
        });
      }

      return blocks.map((block, index) => {
        const durationInMinutes = block.end.diff(block.start, "minutes");
        const height = (durationInMinutes / 60) * 80;

        return (
          <div
            key={index}
            style={{
              height: `${height}px`,
              backgroundColor:
                block.type === "occupied"
                  ? "lightgray"
                  : "rgba(144, 238, 144, 0.71)",
              borderBottom: "1px solid #ccc",
              padding: "2px 4px",
              fontSize: "12px",
              boxSizing: "border-box",
              borderRadius: "20px",
              marginBottom: "2px",
              marginLeft: "10px",
              marginRight: "10px",
              marginTop: "10px",
            }}
          >
            <strong style={{ marginLeft: "10px" }}>
              {block.start.format("HH:mm")} - {block.end.format("HH:mm")}
            </strong>
            {block.type === "occupied" && (
              <div style={{ marginLeft: "10px" }}>
                <div style={{ fontWeight: "bold" }}>
                  {block.classItem.subject_name}
                </div>
                <div>{block.classItem.section_name}</div>
                {/* <div>{block.classItem.class_name}</div> */}
              </div>
            )}
          </div>
        );
      });
    };
    // In the renderWeekView and renderDayView functions, update the renderTimeBlocks function:

    return (
      <div className="table-responsive">
        <div
          className="calendar-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "10px",
          }}
        >
          {/* Header */}
          {weekDays.map((day) => (
            <div
              key={day.format("YYYY-MM-DD")}
              className="calendar-day-header"
              style={{
                padding: "6px",
                borderBottom: "1px solid #ccc",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "14px",
                marginRight: "6px",
                marginLeft: "6px",
                marginBottom: "4px",
                borderRadius: "25px",
              }}
            >
              {day.format("ddd")} <br />
              <span style={{ fontWeight: "normal", fontSize: "12px" }}>
                {day.format("D MMM")}
              </span>
            </div>
          ))}

          {/* Day Columns */}
          {weekDays.map((day) => {
            const dayTimetable = getTimetableForDate(day);

            return (
              <div
                key={day.format("YYYY-MM-DD") + "-classes"}
                style={{
                  border: "1px solid #ccc",
                  height: "100%",
                  borderRadius: "25px",
                  marginRight: "6px",
                  marginLeft: "6px",
                }}
              >
                {renderTimeBlocks(dayTimetable)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayTimetable = getTimetableForDate(currentDate);

    // Sort classes by start time
    const sortedClasses = [...dayTimetable].sort((a, b) =>
      moment(a.start_time, "HH:mm:ss").diff(moment(b.start_time, "HH:mm:ss"))
    );

    const blocks = [];
    let currentTime = moment("07:00", "HH:mm");
    const endTime = moment("23:00", "HH:mm");

    for (let classItem of sortedClasses) {
      const classStart = moment(classItem.start_time, "HH:mm:ss");
      const classEnd = moment(classItem.end_time, "HH:mm:ss");

      // Add free block if gap before class
      if (classStart.isAfter(currentTime)) {
        blocks.push({
          type: "free",
          start: currentTime.clone(),
          end: classStart.clone(),
        });
      }

      // Add occupied block
      blocks.push({
        type: "occupied",
        start: classStart,
        end: classEnd,
        classItem,
      });

      // Move current time to end of class
      currentTime = classEnd.clone();
    }

    // Add final free block if time left after last class
    if (currentTime.isBefore(endTime)) {
      blocks.push({
        type: "free",
        start: currentTime.clone(),
        end: endTime.clone(),
      });
    }

    return (
      <div
        className="calendar-day-view"
        style={{ display: "flex", flexDirection: "column", gap: "10px" }}
      >
        {blocks.map((block, index) => {
          const durationInMinutes = block.end.diff(block.start, "minutes");
          const height = (durationInMinutes / 60) * 40;

          return (
            <div
              key={index}
              style={{
                height: `${height}px`,
                backgroundColor:
                  block.type === "occupied"
                    ? "lightgray"
                    : "rgba(144, 238, 144, 0.71)",
                borderBottom: "1px solid #ccc",
                padding: "4px 8px",
                fontSize: "13px",
                boxSizing: "border-box",
                borderRadius: "20px",
              }}
            >
              <div>
                <strong>
                  {block.start.format("HH:mm")} - {block.end.format("HH:mm")}
                </strong>
              </div>
              {block.type === "occupied" && (
                <div>
                  <div style={{ fontWeight: "bold" }}>
                    {block.classItem.subject_name}
                  </div>
                  <div>{block.classItem.section_name}</div>
                  <div>{block.classItem.class_name}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleDateChange = (e) => {
    if (e.target.value) setCurrentDate(moment(e.target.value));
  };

  return (
    <div className="calendar">
      <div className="calendar-header">
        <div className="bold text-14">
          {view === "month" && currentDate.format("YYYY")}
          {view === "week" && `${currentDate.format("YYYY")}`}
          <span className="mobile-show">
            {view === "day" && `${currentDate.format("ddd")}`}
          </span>
        </div>
        <div className="d-flex gap-2 bold text-14">
          <div onClick={handlePrev} className="custom-symbol">
            <i className="fa fa-angle-left text-18" aria-hidden="true"></i>
          </div>
          <div>
            {(view === "month" || view === "week") &&
              currentDate.format("MMMM")}
            {view === "day" && (
              <div>
                <span
                  onClick={() => {
                    dateInputRef.current.showPicker();
                  }}
                  style={{
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {currentDate.format("DD MMMM YYYY")}
                </span>
              </div>
            )}
          </div>
          <div onClick={handleNext} className="custom-symbol">
            <i className="fa fa-angle-right text-18" aria-hidden="true"></i>
          </div>
          <input
            className="visually-hidden"
            type="date"
            ref={dateInputRef}
            onChange={handleDateChange}
          />
        </div>
        <div className="d-flex gap-1">
          <div
            onClick={() => setView("month")}
            className={`${
              view === "month" && "bold"
            } text-14 border rounded px-1`}
            style={{ cursor: "pointer" }}
          >
            Month
          </div>
          <div
            onClick={() => setView("week")}
            className={`${
              view === "week" && "bold"
            } text-14 border rounded px-1`}
            style={{ cursor: "pointer" }}
          >
            Week
          </div>
          <div
            onClick={() => setView("day")}
            className={`${
              view === "day" && "bold"
            } text-14 border rounded px-1`}
            style={{ cursor: "pointer" }}
          >
            Day
          </div>
        </div>
      </div>
      <div className="mt-2">
        {view === "month" && <RenderMonthView />}
        {view === "week" && renderWeekView()}
        {view === "day" && renderDayView()}
      </div>
    </div>
  );
};

export default TeachersPlanner;
